// formQuestions — the pure ONE-QUESTION-AT-A-TIME projection of an agent form
// (D42). A form arrives as agent-authored STEPS, each holding one or more
// fields; the card shows it one QUESTION at a time instead, with a "2 / 5"
// indicator, Back / Skip / Continue, and Submit on the last question.
//
// A pure render projection over the stored `information_requested` form: no
// event or command change, so it applies retroactively to unanswered forms in
// existing chats. The current-question index is view-ephemeral (seeded from the
// first unanswered question); answers stay in the persisted form draft.

import { exhaustSwitchCase } from "@bilda/utils";
import type { FormDraft } from "../../../app/formDraftStore.js";
import {
    type ChatField,
    type ChatFieldAnswer,
    type ChatStep,
    type FormSourceOption,
    OWN_ANSWER_FIELD_KEY,
} from "../types.js";

// One question = one agent field, plus the step's free-text escape when it
// follows that field (the escape belongs with the choices it is an escape
// from). `step` carries the step's title/note; `firstOfStep` says whether this
// question opens its step (so the step's title/note render once, not per field).
export type FormQuestion = {
    readonly key: string;
    readonly step: ChatStep;
    readonly firstOfStep: boolean;
    readonly fields: readonly ChatField[];
};

const isEscape = (field: ChatField | undefined): boolean =>
    field?.key === OWN_ANSWER_FIELD_KEY;

// Every field opens a question, except an escape that follows another field:
// that one joins the question before it.
const stepQuestions = (step: ChatStep): readonly FormQuestion[] =>
    step.fields.flatMap((field, i) => {
        if (i > 0 && isEscape(field)) return [];
        const next = step.fields[i + 1];
        return [
            {
                key: `${step.key}:${field.key}`,
                step,
                firstOfStep: i === 0,
                fields:
                    next !== undefined && isEscape(next)
                        ? [field, next]
                        : [field],
            },
        ];
    });

// Flatten the form's steps into its ordered questions.
export const toQuestions = (
    steps: readonly ChatStep[],
): readonly FormQuestion[] => steps.flatMap(stepQuestions);

const fieldHasValue = (field: ChatField, draft: FormDraft): boolean =>
    field.type === "field_multi_choice" || field.type === "field_sources"
        ? (draft.multi[field.key] ?? []).length > 0
        : (draft.single[field.key] ?? "").trim() !== "";

// A question counts as answered once any of its fields carries a draft value.
export const isQuestionAnswered = (
    question: FormQuestion,
    draft: FormDraft,
): boolean => question.fields.some((field) => fieldHasValue(field, draft));

// Keep an index inside [0, count - 1] (0 for an empty form).
export const clampQuestionIndex = (index: number, count: number): number =>
    Math.max(0, Math.min(index, count - 1));

// Where the card opens: the first question without a draft answer, or the last
// question (where Submit lives) once every question has one.
export const firstUnansweredIndex = (
    questions: readonly FormQuestion[],
    draft: FormDraft,
): number => {
    const index = questions.findIndex((q) => !isQuestionAnswered(q, draft));
    return index === -1
        ? clampQuestionIndex(questions.length - 1, questions.length)
        : index;
};

// Build the typed answers from the draft, over EVERY field across ALL steps. One
// answer per field that carries a value; a multi-choice field always contributes
// (possibly empty = "none of these"). Total + cast-free — an exhaustive match
// over the field sum.
export const collectAnswers = (
    steps: readonly ChatStep[],
    draft: FormDraft,
    sources: readonly FormSourceOption[],
): readonly ChatFieldAnswer[] => {
    const out: ChatFieldAnswer[] = [];
    for (const step of steps) {
        for (const field of step.fields) {
            switch (field.type) {
                case "field_text": {
                    const v = draft.single[field.key];
                    if (v !== undefined && v.trim() !== "") {
                        out.push({
                            type: "answer_text",
                            key: field.key,
                            value: v,
                        });
                    }
                    break;
                }
                case "field_single_choice": {
                    const v = draft.single[field.key];
                    if (v !== undefined && v !== "") {
                        out.push({
                            type: "answer_single_choice",
                            key: field.key,
                            value: v,
                        });
                    }
                    break;
                }
                case "field_multi_choice":
                    out.push({
                        type: "answer_multi_choice",
                        key: field.key,
                        values: draft.multi[field.key] ?? [],
                    });
                    break;
                case "field_scale": {
                    const v = draft.single[field.key];
                    const n = v === undefined ? Number.NaN : Number(v);
                    if (!Number.isNaN(n)) {
                        out.push({
                            type: "answer_scale",
                            key: field.key,
                            value: n,
                        });
                    }
                    break;
                }
                // Picked ids resolve to the sources they name (with the title
                // the Author saw); an id no longer in the library is dropped.
                case "field_sources": {
                    const ids = draft.multi[field.key] ?? [];
                    out.push({
                        type: "answer_sources",
                        key: field.key,
                        sources: sources.filter((s) => ids.includes(s.id)),
                    });
                    break;
                }
                default:
                    exhaustSwitchCase(field);
            }
        }
    }
    return out;
};

// The answers a question sheet sends (pen "Chat – every entry" §4). On a
// one-choice question your own words are one of the choices: typing there
// selects it, and picking an option afterwards selects that instead, so the
// own answer is dropped when an option is picked. On a several-choices
// question your own words count beside the options.
export const sheetAnswers = (
    steps: readonly ChatStep[],
    draft: FormDraft,
    sources: readonly FormSourceOption[],
): readonly ChatFieldAnswer[] => {
    const optionPicked = steps.some((step) =>
        step.fields.some(
            (field) =>
                field.type === "field_single_choice" &&
                (draft.single[field.key] ?? "") !== "",
        ),
    );
    return collectAnswers(steps, draft, sources).filter(
        (answer) =>
            !(
                optionPicked &&
                answer.type === "answer_text" &&
                answer.key === OWN_ANSWER_FIELD_KEY
            ),
    );
};
